import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, IndianRupee, Receipt, Ticket, Users } from "lucide-react";

function fmtINR(n) {
    return `₹${(Number(n || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function defaultRange() {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    return {
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
    };
}

function StatCard({ Icon, label, value, color }) {
    return (
        <div className="rounded-2xl bg-white border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="h-5 w-5" /></div>
                <div className="text-xs uppercase tracking-wide text-ink-700">{label}</div>
            </div>
            <div className="text-3xl font-heading font-semibold">{value}</div>
        </div>
    );
}

export default function ReportsTab() {
    const [{ from, to }, setRange] = useState(defaultRange());
    const [summary, setSummary] = useState(null);
    const [topProducts, setTopProducts] = useState([]);
    const [gst, setGst] = useState(null);
    const [coupons, setCoupons] = useState([]);
    const [topCustomers, setTopCustomers] = useState([]);

    useEffect(() => {
        const fromIso = `${from}T00:00:00.000Z`;
        const toIso = `${to}T23:59:59.999Z`;
        const params = { from: fromIso, to: toIso };
        api.get("/admin/reports/summary", { params }).then(({ data }) => setSummary(data));
        api.get("/admin/reports/top-products", { params: { ...params, limit: 10 } }).then(({ data }) => setTopProducts(data.items || []));
        api.get("/admin/reports/gst", { params }).then(({ data }) => setGst(data));
        api.get("/admin/reports/coupons").then(({ data }) => setCoupons(data));
        api.get("/admin/reports/customers", { params: { limit: 10 } }).then(({ data }) => setTopCustomers(data));
    }, [from, to]);

    const maxDaily = Math.max(1, ...((summary?.daily || []).map((d) => d.revenue)));

    return (
        <div className="space-y-6" data-testid="reports-tab">
            <div className="flex flex-wrap justify-between items-end gap-3">
                <div>
                    <h3 className="font-heading text-2xl">Reports</h3>
                    <p className="text-sm text-ink-700">Sales, GST, coupon usage and top customers — paid orders only.</p>
                </div>
                <div className="flex items-end gap-3">
                    <div>
                        <Label className="text-xs">From</Label>
                        <Input type="date" value={from} max={to} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="rounded-xl mt-1" data-testid="report-from-input" />
                    </div>
                    <div>
                        <Label className="text-xs">To</Label>
                        <Input type="date" value={to} min={from} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="rounded-xl mt-1" data-testid="report-to-input" />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-summary-cards">
                <StatCard Icon={IndianRupee} label="Revenue" value={fmtINR(summary?.revenue)} color="bg-brand-50 text-brand-700" />
                <StatCard Icon={TrendingUp} label="Orders" value={summary?.orders ?? "—"} color="bg-accent-50 text-accent-600" />
                <StatCard Icon={Receipt} label="GST collected" value={fmtINR(summary?.gst_collected)} color="bg-brand-50 text-brand-700" />
                <StatCard Icon={Ticket} label="Discount given" value={fmtINR(summary?.discount_given)} color="bg-accent-50 text-accent-600" />
            </div>

            {/* Daily revenue */}
            <div className="rounded-3xl bg-white border border-gray-100 p-5">
                <h4 className="font-heading text-lg mb-3">Daily revenue</h4>
                {summary?.daily?.length ? (
                    <div className="flex items-end gap-1.5 h-40">
                        {summary.daily.map((d) => (
                            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                                <div className="text-[10px] text-ink-700 opacity-0 group-hover:opacity-100 transition">{fmtINR(d.revenue)}</div>
                                <div className="w-full rounded-t-md bg-brand-500" style={{ height: `${(d.revenue / maxDaily) * 100}%`, minHeight: 2 }} title={`${d.date}: ${fmtINR(d.revenue)}`} />
                                <div className="text-[9px] text-ink-700 transform -rotate-45 origin-top-left whitespace-nowrap">{d.date.slice(5)}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-ink-700 py-10">No data for this range.</div>
                )}
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                {/* Top products */}
                <div className="rounded-3xl bg-white border border-gray-100 p-5">
                    <h4 className="font-heading text-lg mb-3">Top selling items</h4>
                    {topProducts.length === 0 ? (
                        <div className="text-center text-ink-700 py-6 text-sm">No sales yet.</div>
                    ) : (
                        <div className="space-y-2">
                            {topProducts.map((p, i) => (
                                <div key={`${p.kind}-${p.id}`} className="flex justify-between items-center text-sm border-b border-gray-100 pb-2 last:border-0" data-testid={`report-top-product-${i}`}>
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{p.name}</div>
                                        <div className="text-xs text-ink-700">{p.kind} · {p.qty} sold</div>
                                    </div>
                                    <div className="font-semibold">{fmtINR(p.revenue)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* GST by rate */}
                <div className="rounded-3xl bg-white border border-gray-100 p-5">
                    <h4 className="font-heading text-lg mb-3">GST collected by rate</h4>
                    {!gst || gst.by_rate?.length === 0 ? (
                        <div className="text-center text-ink-700 py-6 text-sm">No GST collected in this range.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-wide text-ink-700">
                                <tr><th className="text-left py-2">Rate</th><th className="text-right py-2">Tax collected</th></tr>
                            </thead>
                            <tbody>
                                {gst.by_rate.map((r) => (
                                    <tr key={r.rate} className="border-t border-gray-100">
                                        <td className="py-2">{r.rate}%</td>
                                        <td className="py-2 text-right font-medium">{fmtINR(r.tax)}</td>
                                    </tr>
                                ))}
                                <tr className="border-t border-gray-100 font-semibold">
                                    <td className="py-2">Total</td>
                                    <td className="py-2 text-right">{fmtINR(gst.total_gst)}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                {/* Coupon usage */}
                <div className="rounded-3xl bg-white border border-gray-100 p-5">
                    <h4 className="font-heading text-lg mb-3">Coupon usage</h4>
                    {coupons.length === 0 ? (
                        <div className="text-center text-ink-700 py-6 text-sm">No coupons created yet.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-wide text-ink-700">
                                <tr>
                                    <th className="text-left py-2">Code</th>
                                    <th className="text-right py-2">Uses</th>
                                    <th className="text-right py-2">Discount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {coupons.map((c) => (
                                    <tr key={c.id} className="border-t border-gray-100">
                                        <td className="py-2 font-mono text-xs">{c.code}</td>
                                        <td className="py-2 text-right">{c.actual_uses || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                                        <td className="py-2 text-right font-medium">{fmtINR(c.discount_given)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Top customers */}
                <div className="rounded-3xl bg-white border border-gray-100 p-5">
                    <h4 className="font-heading text-lg mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-brand-600" /> Top customers</h4>
                    {topCustomers.length === 0 ? (
                        <div className="text-center text-ink-700 py-6 text-sm">No customer purchases yet.</div>
                    ) : (
                        <div className="space-y-2">
                            {topCustomers.map((c, i) => (
                                <div key={c.user_id} className="flex justify-between items-center text-sm border-b border-gray-100 pb-2 last:border-0" data-testid={`report-top-customer-${i}`}>
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{c.user_email}</div>
                                        <div className="text-xs text-ink-700">{c.orders} orders · last {String(c.last_order_at || "").slice(0, 10)}</div>
                                    </div>
                                    <div className="font-semibold">{fmtINR(c.lifetime_value)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
